import { useCallback, useEffect, useMemo, useState } from "react";
import { adminIdentityPort } from "@/adapters/adminIdentityPort";
import { useAuth } from "@/hooks/useAuth";
import type { PermissionChecker } from "@tihany/platform-contracts";
import { APP_PERMISSION_KEYS, type AppPermissionKey, EMPTY_PERMISSION_MATRIX, type PermissionMatrix } from "@/types/permissions";

const allTrueMatrix = (): PermissionMatrix => {
  const next = { ...EMPTY_PERMISSION_MATRIX };
  APP_PERMISSION_KEYS.forEach((key) => {
    next[key] = true;
  });
  return next;
};

export const usePermissions = () => {
  const { user, isAdmin: authIsAdmin, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [matrix, setMatrix] = useState<PermissionMatrix>(EMPTY_PERMISSION_MATRIX);

  const loadPermissions = useCallback(async () => {
    if (authLoading) {
      setLoading(true);
      return;
    }

    if (!user?.id) {
      setIsAdmin(false);
      setMatrix(EMPTY_PERMISSION_MATRIX);
      setLoading(false);
      return;
    }

    setLoading(true);

    // Immediate bypass for admins using the role check from auth hook.
    if (authIsAdmin) {
      setIsAdmin(true);
      setMatrix(allTrueMatrix());
      setLoading(false);
      return;
    }

    const isAdminUser = await adminIdentityPort.isUserAdmin(user.id);
    if (isAdminUser) {
      setIsAdmin(true);
      setMatrix(allTrueMatrix());
      setLoading(false);
      return;
    }

    try {
      const grants = await adminIdentityPort.listPermissionGrants([user.id]);
      const next: PermissionMatrix = { ...EMPTY_PERMISSION_MATRIX };
      grants.forEach((grant) => {
        if (!grant.granted) return;
        const key = grant.permission_key as AppPermissionKey;
        if (key in next) {
          next[key] = true;
        }
      });

      setIsAdmin(false);
      setMatrix(next);
      setLoading(false);
      return;
    } catch {
      setIsAdmin(false);
      setMatrix(EMPTY_PERMISSION_MATRIX);
      setLoading(false);
      return;
    }
  }, [authLoading, user?.id, authIsAdmin]);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  const hasPermission = useCallback(
    (permission: AppPermissionKey | string) => {
      if (isAdmin) return true;
      if (!permission) return false;
      return (matrix as Record<string, boolean>)[permission] ?? false;
    },
    [isAdmin, matrix]
  );

  const hasAny = useCallback(
    (permissions: Array<AppPermissionKey | string>) => {
      if (permissions.length === 0) return false;
      return permissions.some((permission) => hasPermission(permission));
    },
    [hasPermission]
  );

  const hasAll = useCallback(
    (permissions: Array<AppPermissionKey | string>) => {
      if (permissions.length === 0) return true;
      return permissions.every((permission) => hasPermission(permission));
    },
    [hasPermission]
  );

  const permissionChecker = useMemo<PermissionChecker>(
    () => ({
      has: (permission: string) => hasPermission(permission),
      hasAny: (permissions: readonly string[]) => hasAny(Array.from(permissions)),
      hasAll: (permissions: readonly string[]) => hasAll(Array.from(permissions)),
    }),
    [hasPermission, hasAny, hasAll]
  );

  return useMemo(
    () => ({
      loading,
      isAdmin,
      matrix,
      hasPermission,
      hasAny,
      hasAll,
      permissionChecker,
      reloadPermissions: loadPermissions,
    }),
    [loading, isAdmin, matrix, hasPermission, hasAny, hasAll, permissionChecker, loadPermissions]
  );
};

export const usePermissionChecker = (): PermissionChecker => usePermissions().permissionChecker;
